/**
 * PreviewGate.js
 *
 * Password gate for preview/dev deployments ONLY.
 *
 * PRODUCTION SAFETY: This component checks GATSBY_ENV at build time.
 * When GATSBY_ENV=production (set in Vercel for the Production environment),
 * this renders nothing and passes children through untouched.
 * It will NEVER appear on the live production site.
 *
 * Password is set via GATSBY_PREVIEW_PASSWORD env var (Preview/Development only).
 */

import React, { useState, useEffect } from "react"

const COOKIE_NAME = "dc_preview_auth"
const COOKIE_DAYS = 7
const IS_PREVIEW = process.env.GATSBY_ENV !== "production"
const PREVIEW_PASSWORD = process.env.GATSBY_PREVIEW_PASSWORD || ""

const setCookie = (name, value, days) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`
}

const getCookie = name => {
  return document.cookie
    .split("; ")
    .find(row => row.startsWith(`${name}=`))
    ?.split("=")[1]
}

const PreviewGate = ({ children }) => {
  // Not a preview build — pass through immediately, no gate
  if (!IS_PREVIEW) return <>{children}</>

  const [authed, setAuthed] = useState(false)
  const [input, setInput] = useState("")
  const [error, setError] = useState(false)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    if (getCookie(COOKIE_NAME) === "1") {
      setAuthed(true)
    }
    setChecked(true)
  }, [])

  const handleSubmit = e => {
    e.preventDefault()
    if (input === PREVIEW_PASSWORD) {
      setCookie(COOKIE_NAME, "1", COOKIE_DAYS)
      setAuthed(true)
      setError(false)
    } else {
      setError(true)
      setInput("")
    }
  }

  if (!checked) return null
  if (authed) return <>{children}</>

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#240041",
        fontFamily: "sans-serif",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "40px 48px",
          maxWidth: "400px",
          width: "100%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          textAlign: "center",
        }}
      >
        <img
          src="https://www.cormedix.com/wp-content/uploads/2024/12/cormedix_therapeutics_logo.svg"
          alt="CorMedix"
          style={{ height: "36px", marginBottom: "24px" }}
        />
        <p style={{ color: "#555", marginBottom: "24px", fontSize: "15px" }}>
          This is a preview site for internal review only.
          <br />
          Enter the password to continue.
        </p>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={input}
            onChange={e => { setInput(e.target.value); setError(false) }}
            placeholder="Password"
            autoFocus
            style={{
              width: "100%",
              padding: "12px 16px",
              fontSize: "16px",
              border: error ? "2px solid #c0392b" : "1.5px solid #ccc",
              borderRadius: "8px",
              marginBottom: "12px",
              boxSizing: "border-box",
              outline: "none",
            }}
          />
          {error && (
            <p style={{ color: "#c0392b", fontSize: "13px", marginBottom: "12px" }}>
              Incorrect password. Please try again.
            </p>
          )}
          <button
            type="submit"
            style={{
              width: "100%",
              padding: "12px",
              background: "#240041",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Enter
          </button>
        </form>
      </div>
    </div>
  )
}

export default PreviewGate
